import React from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  BoxProps,
  Grid,
  CircularProgress,
  CircularProgressProps,
  Typography,
  styled,
} from "@mui/material"

import Dot from "components/ui/Dot"
import { colors } from "theme"

const SIZE = 234
const THICKNESS = 8

type Props = React.HTMLAttributes<HTMLDivElement> &
  CircularProgressProps & {
    top: string
    amount: string
    bottom?: string
    overdue?: number
  }

const DoughnutBackground = styled((props: CircularProgressProps) => (
  <CircularProgress
    size={SIZE}
    thickness={THICKNESS}
    variant="determinate"
    value={100}
    {...props}
  />
))(() => ({
  color: colors.cultured,
}))

const DoughnutOverdue = styled((props: CircularProgressProps) => (
  <CircularProgress
    size={SIZE}
    thickness={THICKNESS}
    variant="determinate"
    {...props}
  />
))(() => ({
  color: colors.ruby,
}))

const DoughnutProgress = styled((props: CircularProgressProps) => (
  <CircularProgress
    size={SIZE}
    thickness={THICKNESS}
    variant="determinate"
    {...props}
  />
))(() => ({}))

const DoughnutLegend = styled((props: BoxProps) => (
  <Box {...props}>{props.children}</Box>
))(() => ({
  margin: "0 auto 15px auto",
  width: "100%",
}))

const DoughnutComponent = ({
  top,
  bottom,
  amount,
  value,
  overdue,
  ...restProps
}: Props) => {
  const { t } = useTranslation()

  const { color } = restProps

  const isPaid = value != undefined && value != 0
  const isOverdue = overdue != undefined && overdue != 0
  const isUnpaid = color != undefined && color === "error"
  const isEnded =
    color != undefined &&
    color === "primary" &&
    value != undefined &&
    value != 0

  return (
    <Container>
      <Content>
        <Doughnuts>
          <DoughnutBackground />
          <DoughnutOverdue
            value={overdue}
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
            }}
          />
          <DoughnutProgress
            value={value}
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
            }}
            {...restProps}
          />
        </Doughnuts>
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="caption" component="span">
              {top}
            </Typography>
            <Typography
              variant="h4"
              component="span"
              sx={{
                margin: "5px 0",
              }}
            >
              {amount}
            </Typography>
            <Typography variant="caption" component="span">
              {bottom}
            </Typography>
          </Grid>
        </Box>
      </Content>
      <DoughnutLegend>
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="flex-end"
        >
          {isUnpaid ? (
            <Grid item>
              <Dot color="error" />
              <Typography variant="caption" component="span">
                {t("features.debt.doughnut.legend.unpaid.title")}
              </Typography>
            </Grid>
          ) : (
            <>
              {isEnded ? (
                <Grid item>
                  <Dot color="primary" />
                  <Typography variant="caption" component="span">
                    {t("features.debt.doughnut.legend.paid.title")}
                  </Typography>
                </Grid>
              ) : (
                <>
                  <Grid item>
                    <Dot />
                    <Typography variant="caption" component="span">
                      {t("features.debt.doughnut.legend.remain.title")}
                    </Typography>
                  </Grid>
                  {isPaid && (
                    <Grid item>
                      <Dot color="primary" />
                      <Typography variant="caption" component="span">
                        {t("features.debt.doughnut.legend.paid.title")}
                      </Typography>
                    </Grid>
                  )}
                  {isOverdue && (
                    <Grid item>
                      <Dot color="error" />
                      <Typography variant="caption" component="span">
                        {t("features.debt.doughnut.legend.overdue.title")}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
            </>
          )}
        </Grid>
      </DoughnutLegend>
    </Container>
  )
}

export default DoughnutComponent

export const Container = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
}))

export const Content = styled(Box)(() => ({
  position: "relative",
  display: "inline-flex",
  width: "100%",
}))

export const Doughnuts = styled(Box)(() => ({
  position: "relative",
  margin: "0 auto",
}))
